'use client';

import { getFragmentData } from '@/__generated__';
import { EventCardFragment } from '@/__generated__/graphql';
import { GENRE_CATEGORY_FRAGMENT } from '@/gql/fragments/categories.gql';
import { ENTRY_BASE_FRAGMENT } from '@/gql/fragments/entries.gql';
import { parseEventTimeFragment } from '@/lib/parsers/common/parseEventTime';
import useShortlistStore from '@/lib/store/shortlist';
import { fmtEventDateRange, fmtEventTime, simplifyCategory } from '@/lib/utils/format';
import { parseUri, toString } from '@liquorice/utils';
import { useTranslations } from 'next-intl';
import Card from '../Card';
import { OverrideCardProps } from '../EntryCards';
import PageTicket from '../PageTicket';
import Box from '../ui/Box';
import Btn from '../ui/Btn';
import * as styles from './Cards.css';

export const EventCard = ({
  data,
  overrides,
}: {
  data?: EventCardFragment;
  overrides?: OverrideCardProps;
}) => {
  const {
    entryImage,
    entryTitle,
    eventTime,
    eventDateRange: maybeEventDateRange,
    status,
    ...base
  } = data ?? {};
  const {
    heading: maybeTitle,
    image: maybeImage,
    // content:maybeContent
  } = overrides ?? {};

  const { uri, id } = getFragmentData(ENTRY_BASE_FRAGMENT, base);

  const href = parseUri(uri);

  const t = useTranslations('common');
  const genres = simplifyCategory(GENRE_CATEGORY_FRAGMENT, data?.genreCategory);
  const eventTimeRange = fmtEventTime(parseEventTimeFragment(eventTime));
  const eventDateRange = fmtEventDateRange(maybeEventDateRange);
  const toggleEvent = useShortlistStore((s) => s.toggleEvent);
  const disabled = status === 'expired';

  if (!data) return null;

  return (
    <Card
      className={styles.root}
      disableLinkWrap
      elevation={false}
      fullHeight
      colorSet="white"
      paper
      item={{
        href,
        title: maybeTitle ?? entryTitle,
        image: maybeImage ?? entryImage,
        meta: genres?.map((genre) => genre.title).join(', '),
        description: eventDateRange ?? eventTimeRange,
        disabled,
      }}>
      <Box cx={{ position: 'relative' }}>
        <Card.Meta
          className={styles.tag}
          color="shiraz"
          uppercase
          weight="semibold"
          variant="tiny"
        />
        <Card.Heart
          itemID={toString(id)}
          onClick={() => toggleEvent(data)}
          className={styles.heart}
          cx={{ m: '3xs' }}
          style={{ position: 'absolute' }}
        />
        <Card.Disabled className={styles.disabled} color="white" uppercase variant="tiny" />
        <Card.Image ratio="landscape" enableLinkWrap />
      </Box>
      <Card.Body className={styles.body({ withTransition: !disabled })} colorSet="white" paper>
        <Card.Title variant="medium" weight="semibold" cx={{ mY: '4xs' }} />
        <Card.Description variant="tiny" weight="regular" uppercase />
        <Box colorSet="white" paper className={styles.cta}>
          {href && (
            <Box cx={{ width: '40' }}>
              <Btn
                as="a"
                href={href}
                variant="outlined"
                color="black"
                size="tiny"
                weight="semibold"
                uppercase
                fullWidth>
                {t('moreInfo')}
              </Btn>
            </Box>
          )}
          <PageTicket
            data={data}
            cx={{ width: 'full' }}
            view="compact"
            TicketBtnProps={{
              size: 'tiny',
              fullWidth: true,
              weight: 'semibold',
              color: 'black',
            }}
          />
        </Box>
      </Card.Body>
    </Card>
  );
};
